<template>
  <div class="kitchen-modal">
    <!-- Left side: Image Gallery -->
    <div class="kitchen-modal__gallery">
      <div
        v-if="kitchen.images && kitchen.images.length > 0"
        class="kitchen-modal__gallery-wrapper">
        <!-- Swiper for images -->
        <div
          ref="swiperEl"
          class="kitchen-modal__swiper swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(image, index) in kitchen.images"
              :key="`slide-${index}`"
              class="swiper-slide">
              <picture class="kitchen-modal__picture">
                <source
                  v-if="image.avifLarge"
                  type="image/avif"
                  :srcset="image.avifLarge" />
                <source
                  v-if="image.webpLarge"
                  type="image/webp"
                  :srcset="image.webpLarge" />
                <img
                  :src="image.srcLarge || image.src"
                  :alt="image.alt || `${kitchen.title} - Image ${index + 1}`"
                  class="kitchen-modal__image"
                  loading="lazy" />
              </picture>
            </div>
          </div>
        </div>

        <!-- Slider controls -->
        <div
          v-if="kitchen.images.length > 1"
          class="kitchen-modal__controls">
          <button
            class="kitchen-modal__control-btn"
            :class="{ 'kitchen-modal__control-btn--disabled': currentSlide === 0 }"
            :disabled="currentSlide === 0"
            aria-label="Previous slide"
            @click="handlePrev">
            <caret-left-icon class="kitchen-modal__control-icon" />
          </button>
          <span class="kitchen-modal__counter">
            <span class="kitchen-modal__current">{{ formatSlideNumber(currentSlide + 1) }}</span>
            <span class="kitchen-modal__separator">/</span>
            <span class="kitchen-modal__total">{{ formatSlideNumber(kitchen.images.length) }}</span>
          </span>
          <button
            class="kitchen-modal__control-btn"
            :class="{
              'kitchen-modal__control-btn--disabled': currentSlide === kitchen.images.length - 1,
            }"
            :disabled="currentSlide === kitchen.images.length - 1"
            aria-label="Next slide"
            @click="handleNext">
            <caret-right-icon class="kitchen-modal__control-icon" />
          </button>
        </div>
      </div>
    </div>

    <!-- Right side: Details -->
    <div class="kitchen-modal__details">
      <!-- Close button (sticky) -->
      <div class="kitchen-modal__close-wrapper">
        <button
          class="kitchen-modal__close-btn"
          :aria-label="$t('general.close')"
          @click="$emit('close')">
          <icon-close class="kitchen-modal__close-icon" />
          <span class="kitchen-modal__close-text">{{ $t("general.close") }}</span>
        </button>
      </div>

      <!-- Content -->
      <div class="kitchen-modal__content">
        <!-- Title -->
        <h2
          v-if="kitchen.title"
          class="kitchen-modal__title">
          {{ kitchen.title }}
        </h2>

        <!-- Description Section -->
        <div
          v-if="kitchen.description"
          class="kitchen-modal__section">
          <div
            class="kitchen-modal__description"
            v-html="kitchen.description" />
        </div>

        <!-- Finishes and Appliances -->
        <div
          v-if="kitchen.finishes || kitchen.appliances"
          class="kitchen-modal__meta">
          <div
            v-if="kitchen.finishes"
            class="kitchen-modal__meta-item">
            <div class="kitchen-modal__meta-label">
              {{ $t("dealer.modal.finishes") }}
            </div>
            <div class="kitchen-modal__meta-value">
              {{ kitchen.finishes }}
            </div>
          </div>
          <div
            v-if="kitchen.appliances"
            class="kitchen-modal__meta-item">
            <div class="kitchen-modal__meta-label">
              {{ $t("dealer.modal.appliances") }}
            </div>
            <div class="kitchen-modal__meta-value">
              {{ kitchen.appliances }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import CaretLeftIcon from '~/assets/svgs/icon/black/caret-left.svg'
  import CaretRightIcon from '~/assets/svgs/icon/black/caret-right.svg'
  import IconClose from '~/assets/svgs/icon/black/x.svg'

  const props = defineProps({
    kitchen: {
      type: Object,
      required: true
    }
  })

  defineEmits([ 'close' ])

  // Swiper setup
  const swiperEl = ref(null)
  const currentSlide = ref(0)
  const swiperOptions = {
    slidesPerView: 1,
    loop: false,
    allowTouchMove: true,
    spaceBetween: 0,
    watchSlidesProgress: true,
    autoHeight: false,
    width: null,
    observer: true,
    observeParents: true,
    centeredSlides: false,
    freeMode: false
  }

  const {
    handleNext, handlePrev, currentSlide: swiperCurrentSlide
  } = useSwiper(
    swiperEl,
    swiperOptions
  )

  // Watch for changes from swiper and update our local ref
  onMounted(() => {
    nextTick(() => {
      if (swiperCurrentSlide.value !== undefined) {
        currentSlide.value = swiperCurrentSlide.value
      }
    })
  })

  // Watch swiperCurrentSlide changes
  watch(swiperCurrentSlide, newVal => {
    if (newVal !== undefined) {
      currentSlide.value = newVal
    }
  })

  const formatSlideNumber = num => {
    return num < 10 ? `0${num}` : `${num}`
  }
</script>

<style lang="scss" scoped>
.kitchen-modal {
  display: flex;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background-color: var(--color-light-grey, #fafafa);
  overflow: hidden;

  @include up-to-tablet {
    flex-direction: column-reverse;
    height: auto;
    min-height: 100vh;
    max-height: none;
    overflow: visible;
  }
}

.kitchen-modal__gallery {
  flex: 0 0 60%;
  background-color: var(--color-white, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--size-grid-margin, 40px);
  position: relative;
  overflow: hidden;
  height: 100%;

  @include up-to-tablet {
    flex: 0 0 auto;
    padding: 0;
    align-items: flex-start;
    height: auto;
    overflow: visible;
  }
}

.kitchen-modal__gallery-wrapper {
  width: 100%;
  max-width: 770px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--spacers-space-m, 40px);

  @include up-to-tablet {
    max-width: 100%;
    gap: var(--spacers-space-s, 24px);
  }
}

.kitchen-modal__swiper {
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;

  @include up-to-tablet {
    width: 100vw;
    aspect-ratio: 16/9;
  }

  :deep(.swiper-wrapper) {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: stretch;
  }

  :deep(.swiper-slide) {
    width: 100%;
    height: 100%;
    flex-shrink: 0;
    position: relative;
    display: block;
    overflow: hidden;
  }
}

.kitchen-modal__picture {
  width: 100%;
  height: 100%;
  display: block;
}

.kitchen-modal__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.kitchen-modal__controls {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacers-space-ss, 12px);
  padding: 0 var(--spacers-space-m, 40px);

  @include up-to-tablet {
    padding: 0 var(--spacers-space-m, 40px) var(--spacers-space-m, 40px);
  }
}

.kitchen-modal__control-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-black, #1d1d1b);
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-height: 16px;
  opacity: 1;
  transition: opacity 0.3s;

  &:hover:not(:disabled) {
    opacity: 0.6;
  }

  &--disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
}

.kitchen-modal__control-icon {
  width: 16px;
  height: 16px;
  display: block;

  :deep(path) {
    stroke-width: 2;
  }
}

.kitchen-modal__counter {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--typography-size-specials-ui-kit-16, 16px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-book, 400);
  line-height: normal;
}

.kitchen-modal__current {
  color: var(--color-red, #dc0814);
}

.kitchen-modal__separator,
.kitchen-modal__total {
  color: var(--color-black, #1d1d1b);
}

.kitchen-modal__details {
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  background-color: var(--color-light-grey, #fafafa);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 100%;
  -webkit-overflow-scrolling: touch;

  @include up-to-tablet {
    flex: 1;
    overflow-y: visible;
    height: auto;
  }
}

.kitchen-modal__close-wrapper {
  position: sticky;
  top: 0;
  background-color: var(--color-light-grey, #fafafa);
  padding: var(--spacers-space-l, 48px) var(--size-grid-margin, 40px);
  display: flex;
  justify-content: flex-end;
  z-index: 2;

  @include up-to-tablet {
    padding: var(--spacers-space-m, 40px) var(--spacers-space-m, 40px);
  }
}

.kitchen-modal__close-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacers-space-xxs, 8px);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--color-black, #1d1d1b);
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.6;
  }
}

.kitchen-modal__close-icon {
  width: 24px;
  height: 24px;
}

.kitchen-modal__close-text {
  font-size: var(--typography-size-specials-ui-kit-14, 14px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-medium, 500);
  text-transform: uppercase;
  line-height: normal;
}

.kitchen-modal__content {
  flex: 1;
  padding: 0 var(--size-grid-margin, 40px) var(--spacers-space-xl, 64px);
  display: flex;
  flex-direction: column;
  gap: var(--spacers-space-l, 48px);

  @include up-to-tablet {
    padding: 0 var(--spacers-space-m, 40px) var(--spacers-space-l, 48px);
    gap: var(--spacers-space-m, 40px);
  }
}

.kitchen-modal__title {
  font-size: var(--typography-size-l, 40px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-medium, 500);
  line-height: 1.4;
  color: var(--color-black, #1d1d1b);
  margin: 0;

  @include up-to-tablet {
    font-size: var(--typography-size-m, 32px);
  }
}

.kitchen-modal__section {
  display: flex;
  flex-direction: column;
  gap: var(--spacers-space-m, 40px);
}

.kitchen-modal__description {
  font-size: var(--typography-size-s, 18px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-book, 400);
  line-height: 1.6;
  color: var(--color-black, #1d1d1b);

  :deep(p) {
    margin: 0;
  }

  :deep(p + p) {
    margin-top: 1em;
  }
}

.kitchen-modal__meta {
  display: flex;
  gap: var(--size-grid-gutter, 32px);

  @include up-to-tablet {
    flex-direction: column;
    gap: var(--spacers-space-s, 24px);
  }
}

.kitchen-modal__meta-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacers-space-xxxs, 4px);
}

.kitchen-modal__meta-label {
  font-size: var(--typography-size-specials-ui-kit-14, 14px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-medium, 500);
  text-transform: uppercase;
  line-height: normal;
  color: var(--color-black, #1d1d1b);
}

.kitchen-modal__meta-value {
  font-size: var(--typography-size-s, 18px);
  font-family: var(--font-family-poppins);
  font-weight: var(--typography-weight-book, 400);
  line-height: 1.6;
  color: var(--color-black, #1d1d1b);
}
</style>
